import { z } from 'zod'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { useBoolean } from '@/hooks/use-boolean'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useDeepEffect } from '@/hooks/use-deep-effect'
import LoadingSection from '@/components/loading-indicator/loading-section'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  PlatformConfig,
  PlatformConfigName,
} from '@/types/platform-config.type'

interface ReferralSettingsFormValues {
  referralPercent: number
}

interface PlatformFeeSettingsFormValues {
  platformFee: number
}

const referralSettingsSchema = z.object({
  referralPercent: z
    .number({ invalid_type_error: 'กรุณากรอกตัวเลข' })
    .min(0, { message: 'ต้องไม่น้อยกว่า 0%' })
    .max(100, { message: 'ต้องไม่เกิน 100%' }),
})

const platformFeeSettingsSchema = z.object({
  platformFee: z
    .number({ invalid_type_error: 'กรุณากรอกตัวเลข' })
    .min(0, { message: 'ต้องไม่น้อยกว่า 0%' })
    .max(100, { message: 'ต้องไม่เกิน 100%' }),
})

export default function SettingsView() {
  const isFetching = useBoolean(true)
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ReferralSettingsFormValues>({
    resolver: zodResolver(referralSettingsSchema),
    defaultValues: {
      referralPercent: 0,
    },
  })

  const {
    control: platformFeeControl,
    handleSubmit: handlePlatformFeeSubmit,
    setValue: setPlatformFeeValue,
    formState: {
      errors: platformFeeErrors,
      isSubmitting: isPlatformFeeSubmitting,
    },
  } = useForm<PlatformFeeSettingsFormValues>({
    resolver: zodResolver(platformFeeSettingsSchema),
    defaultValues: {
      platformFee: 0,
    },
  })

  const onSubmit = async (inputData: ReferralSettingsFormValues) => {
    try {
      const { data } = await axios.post<PlatformConfig>(
        endpoints.platformConfig.create,
        {
          name: PlatformConfigName.REFERRAL_PERCENT,
          value: String(inputData.referralPercent),
        },
      )

      setValue('referralPercent', Number(data.value))

      toast.success('บันทึกข้อมูลสำเร็จ')
    } catch {
      toast.error('เกิดข้อผิดพลาดในการบันทึกข้อมูล')
    }
  }

  const onPlatformFeeSubmit = async (
    inputData: PlatformFeeSettingsFormValues,
  ) => {
    try {
      const { data } = await axios.post<PlatformConfig>(
        endpoints.platformConfig.create,
        {
          name: PlatformConfigName.PLATFORM_FEE,
          value: String(inputData.platformFee),
        },
      )

      setPlatformFeeValue('platformFee', Number(data.value))

      toast.success('บันทึกข้อมูลสำเร็จ')
    } catch {
      toast.error('เกิดข้อผิดพลาดในการบันทึกข้อมูล')
    }
  }

  useDeepEffect(() => {
    const fetchPlatformConfig = async () => {
      try {
        isFetching.onTrue()
        const { data } = await axios.get<PlatformConfig[]>(
          endpoints.platformConfig.list,
          {
            params: {
              name: `${PlatformConfigName.REFERRAL_PERCENT},${PlatformConfigName.PLATFORM_FEE}`,
            },
          },
        )

        // Set form values from fetched data
        if (data && data.length > 0) {
          const referralConfig = data.find(
            (config) => config.name === PlatformConfigName.REFERRAL_PERCENT,
          )

          if (referralConfig && referralConfig.value) {
            setValue('referralPercent', Number(referralConfig.value))
          }

          const platformFeeConfig = data.find(
            (config) => config.name === PlatformConfigName.PLATFORM_FEE,
          )

          if (platformFeeConfig && platformFeeConfig.value) {
            setPlatformFeeValue('platformFee', Number(platformFeeConfig.value))
          }
        }
      } catch (error) {
        console.error('Failed to fetch platform config:', error)
        toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล')
      } finally {
        isFetching.onFalse()
      }
    }
    fetchPlatformConfig()
  }, [setValue, setPlatformFeeValue])

  return (
    <LoadingSection loading={isFetching.value}>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>รหัสอ้างอิง</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <Label htmlFor="referralPercent">
                  เปอร์เซ็นต์ผลตอบแทนรหัสอ้างอิง (%)
                </Label>
                <Controller
                  name="referralPercent"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="referralPercent"
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      className="mt-2 w-40"
                      disabled={isSubmitting}
                      onKeyDown={(e) => {
                        // Prevent typing "-", "e", "E"
                        if (['-', 'e', 'E'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                      onChange={(e) => {
                        let value = e.target.value

                        // Add leading zero if user starts with decimal point
                        if (value.startsWith('.')) {
                          value = '0' + value
                          // Update the input field to show the formatted value
                          e.target.value = value
                        }

                        // Check if decimal places exceed 2 digits
                        if (value.includes('.')) {
                          const parts = value.split('.')
                          if (parts[1] && parts[1].length > 2) {
                            return // Don't update if more than 2 decimal places
                          }
                        }

                        field.onChange(value === '' ? '' : Number(value))
                      }}
                    />
                  )}
                />
                {errors.referralPercent && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.referralPercent.message}
                  </p>
                )}
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'กำลังบันทึก...' : 'บันทึก'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>ค่าธรรมเนียมแพลตฟอร์ม</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handlePlatformFeeSubmit(onPlatformFeeSubmit)}
              className="space-y-6"
            >
              <div>
                <Label htmlFor="platformFee">
                  เปอร์เซ็นต์ค่าธรรมเนียมแพลตฟอร์ม (%)
                </Label>
                <Controller
                  name="platformFee"
                  control={platformFeeControl}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="platformFee"
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      className="mt-2 w-40"
                      disabled={isPlatformFeeSubmitting}
                      onKeyDown={(e) => {
                        // Prevent typing "-", "e", "E"
                        if (['-', 'e', 'E'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                      onChange={(e) => {
                        let value = e.target.value

                        // Add leading zero if user starts with decimal point
                        if (value.startsWith('.')) {
                          value = '0' + value
                          // Update the input field to show the formatted value
                          e.target.value = value
                        }

                        // Check if decimal places exceed 2 digits
                        if (value.includes('.')) {
                          const parts = value.split('.')
                          if (parts[1] && parts[1].length > 2) {
                            return // Don't update if more than 2 decimal places
                          }
                        }

                        field.onChange(value === '' ? '' : Number(value))
                      }}
                    />
                  )}
                />
                {platformFeeErrors.platformFee && (
                  <p className="text-sm text-red-500 mt-1">
                    {platformFeeErrors.platformFee.message}
                  </p>
                )}
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={isPlatformFeeSubmitting}>
                  {isPlatformFeeSubmitting ? 'กำลังบันทึก...' : 'บันทึก'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </LoadingSection>
  )
}
